import React, { useRef, useEffect, useCallback, useState } from 'react';
import '../styles/LessonEditor.css';

const LessonEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const savedSel  = useRef(null);

  const [ytModal,   setYtModal]   = useState(false);
  const [ytUrl,     setYtUrl]     = useState('');
  const [imgModal,  setImgModal]  = useState(false);
  const [imgUrl,    setImgUrl]    = useState('');
  const [linkModal, setLinkModal] = useState(false);
  const [linkUrl,   setLinkUrl]   = useState('');
  const [linkText,  setLinkText]  = useState('');

  // Helper para limpiar HTML: decodificar entidades y remover contenido corrupto
  const cleanHtml = (html) => {
    if (!html) return '';
    // Decodificar entidades HTML
    const decoded = html
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    // Remover URLs sueltas (no dentro de tags) al inicio
    return decoded.replace(/^https?:\/\/[^\s<]+\s*/, '');
  };

  // Sincroniza cambios externos (abrir otra leccion, editar existente, limpiar formulario)
  useEffect(() => {
    if (!editorRef.current) return;
    const nextHtml = cleanHtml(value || '');
    if (editorRef.current.innerHTML !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
  }, [value]);

  const emit = useCallback(() => {
    if (editorRef.current) {
      let html = editorRef.current.innerHTML;
      // Limpiar HTML antes de emitir
      html = cleanHtml(html);
      // Remover párrafos vacíos al inicio/final
      html = html.replace(/^<p><br><\/p>/g, '').replace(/<p><br><\/p>$/g, '');
      const preview = html.substring(0, 150).replace(/</g, '&lt;');
      console.log('LessonEditor emit - contenido:', preview + '...');
      console.log('LessonEditor emit - longitud:', html.length);
      onChange(html);
    }
  }, [onChange]);

  const saveSel = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSel.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSel = () => {
    editorRef.current.focus();
    if (!savedSel.current) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedSel.current);
  };

  const exec = (cmd, val) => {
    editorRef.current.focus();
    document.execCommand(cmd, false, val || null);
    emit();
  };

  const insertBlock = (html) => {
    restoreSel();
    document.execCommand('insertHTML', false, html);
    // Usar setTimeout para permitir que el DOM actualice antes de emit
    setTimeout(() => {
      emit();
    }, 0);
  };

  const parseVideoEmbedUrl = (rawUrl) => {
    const url = (rawUrl || '').trim();
    if (!url) return null;

    // YouTube: watch/embed/shorts/youtu.be
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) {
      return {
        src: 'https://www.youtube.com/embed/' + ytMatch[1],
        provider: 'youtube',
      };
    }

    // Google Drive: .../file/d/<id>/view or .../file/d/<id>/preview
    const driveFileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (driveFileMatch) {
      return {
        src: 'https://drive.google.com/file/d/' + driveFileMatch[1] + '/preview?rm=minimal',
        provider: 'drive',
      };
    }

    // Google Drive direct preview link
    if (/drive\.google\.com\/file\/d\/[a-zA-Z0-9_-]+\/preview/.test(url)) {
      const clean = url.replace(/\?.*$/, '');
      return {
        src: clean + '?rm=minimal',
        provider: 'drive',
      };
    }

    return null;
  };

  const handleYT = () => {
    const url = ytUrl.trim();
    const parsed = parseVideoEmbedUrl(url);
    if (!parsed) {
      alert('Enlace de video no valido.\nAcepta: YouTube o Google Drive (preview/view).');
      return;
    }
    if (parsed.provider === 'drive') {
      insertBlock(
        '<p><iframe src="' + parsed.src + '" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen style="width:100%;aspect-ratio:16/9;border-radius:8px;"></iframe></p><p><br></p>'
      );
    } else {
      insertBlock(
        '<p><iframe src="' + parsed.src + '" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen style="width:100%;aspect-ratio:16/9;border-radius:8px;"></iframe></p><p><br></p>'
      );
    }
    setYtUrl(''); setYtModal(false);
  };

  const handleImg = () => {
    const url = imgUrl.trim();
    if (!url) return;
    insertBlock('<p><img src="' + url + '" alt="imagen" style="max-width:100%;border-radius:8px;" /></p><p><br></p>');
    setImgUrl(''); setImgModal(false);
  };

  const handleLink = () => {
    const url  = linkUrl.trim();
    const text = linkText.trim() || url;
    if (!url) return;
    insertBlock('<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + text + '</a>');
    setLinkUrl(''); setLinkText(''); setLinkModal(false);
  };

  return (
    <div className="le-wrap">

      <div className="le-toolbar">
        <div className="le-group">
          <button type="button" className="le-btn le-b"    title="Negrita"  onClick={() => exec('bold')}>B</button>
          <button type="button" className="le-btn le-i"    title="Cursiva"  onClick={() => exec('italic')}>I</button>
          <button type="button" className="le-btn le-u"    title="Subrayado" onClick={() => exec('underline')}>U</button>
        </div>
        <span className="le-sep" />
        <div className="le-group">
          <button type="button" className="le-btn" title="Titulo grande"   onClick={() => exec('formatBlock', 'h2')}>Titulo 1</button>
          <button type="button" className="le-btn" title="Titulo mediano"  onClick={() => exec('formatBlock', 'h3')}>Titulo 2</button>
          <button type="button" className="le-btn" title="Parrafo normal"  onClick={() => exec('formatBlock', 'p')}>Normal</button>
        </div>
        <span className="le-sep" />
        <div className="le-group">
          <button type="button" className="le-btn" title="Lista con puntos"  onClick={() => exec('insertUnorderedList')}>Lista</button>
          <button type="button" className="le-btn" title="Lista numerada"    onClick={() => exec('insertOrderedList')}>1. Numerada</button>
          <button type="button" className="le-btn" title="Cita / destacado"  onClick={() => exec('formatBlock', 'blockquote')}>Cita</button>
        </div>
        <span className="le-sep" />
        <div className="le-group">
          <button type="button" className="le-btn le-btn-yt"   title="Insertar video (YouTube o Drive)"
            onClick={() => { saveSel(); setYtModal(true); }}>
            VIDEO
          </button>
          <button type="button" className="le-btn le-btn-img"  title="Insertar imagen"
            onClick={() => { saveSel(); setImgModal(true); }}>
            IMAGEN
          </button>
          <button type="button" className="le-btn le-btn-link" title="Insertar enlace"
            onClick={() => { saveSel(); setLinkModal(true); }}>
            ENLACE
          </button>
          <button type="button" className="le-btn" title="Linea separadora"
            onClick={() => insertBlock('<hr style="border:none;border-top:2px solid #e2e8f0;margin:20px 0;"/><p><br></p>')}>
            --- Linea
          </button>
        </div>
      </div>

      <div
        ref={editorRef}
        className="le-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        data-placeholder="Escribe aqui el contenido de la leccion... Usa los botones de arriba para formato, videos e imagenes."
      />

      {ytModal && (
        <div className="le-overlay" onClick={() => setYtModal(false)}>
          <div className="le-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="le-modal-title">Insertar video</h3>
            <p className="le-modal-desc">Pega el enlace del video:</p>
            <input type="url" className="le-modal-input" autoFocus
              placeholder="https://www.youtube.com/watch?v=... o https://drive.google.com/file/d/.../preview"
              value={ytUrl} onChange={(e) => setYtUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleYT()} />
            <p className="le-modal-hint">Acepta YouTube (watch, shorts, youtu.be) y Google Drive (view/preview)</p>
            <div className="le-modal-btns">
              <button type="button" className="le-modal-cancel" onClick={() => setYtModal(false)}>Cancelar</button>
              <button type="button" className="le-modal-ok"     onClick={handleYT}>Insertar video</button>
            </div>
          </div>
        </div>
      )}

      {imgModal && (
        <div className="le-overlay" onClick={() => setImgModal(false)}>
          <div className="le-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="le-modal-title">Insertar imagen</h3>
            <p className="le-modal-desc">Pega la URL de la imagen:</p>
            <input type="url" className="le-modal-input" autoFocus
              placeholder="https://ejemplo.com/imagen.jpg"
              value={imgUrl} onChange={(e) => setImgUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleImg()} />
            <p className="le-modal-hint">La imagen debe ser una URL publica accesible</p>
            <div className="le-modal-btns">
              <button type="button" className="le-modal-cancel" onClick={() => setImgModal(false)}>Cancelar</button>
              <button type="button" className="le-modal-ok"     onClick={handleImg}>Insertar imagen</button>
            </div>
          </div>
        </div>
      )}

      {linkModal && (
        <div className="le-overlay" onClick={() => setLinkModal(false)}>
          <div className="le-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="le-modal-title">Insertar enlace</h3>
            <p className="le-modal-desc">URL:</p>
            <input type="url" className="le-modal-input" autoFocus
              placeholder="https://ejemplo.com"
              value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
            <p className="le-modal-desc" style={{marginTop:'12px'}}>Texto visible (opcional):</p>
            <input type="text" className="le-modal-input"
              placeholder="Texto del enlace"
              value={linkText} onChange={(e) => setLinkText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLink()} />
            <div className="le-modal-btns">
              <button type="button" className="le-modal-cancel" onClick={() => setLinkModal(false)}>Cancelar</button>
              <button type="button" className="le-modal-ok"     onClick={handleLink}>Insertar enlace</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LessonEditor;